import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useBranch } from "../context/BranchContext";
import { PageHeader, StatCard } from "../components/shared";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Download, Printer, ChevronLeft, Users, CheckCircle2, TrendingUp, XCircle } from "lucide-react";
import { ReportDateFilter, useReportRange } from "../components/ReportDateFilter";


function downloadCsv(name, head, rows) {
  const csv = [head, ...rows].map((r) => r.map((c) => `"${String(c ?? "")}"`).join(",")).join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = `${name}.csv`; a.click();
}

// report.preset = {status?, source?} — controls which slice of enquiries to show
export default function EnquiryReport({ report, onBack }) {
  const { branchParam, selected, branches } = useBranch();
  const branchLabel = (selected && selected !== "all") ? (branches.find((b) => b.id === selected)?.name || "Your Branch") : "All Branches";
  const rr = useReportRange("month");
  const { start, end } = rr;
  const [data, setData] = useState(null);
  const preset = report.preset || {};

  const load = useCallback(() => {
    const params = { branch_id: branchParam, start, end };
    if (preset.status) params.status = preset.status;
    if (preset.source) params.source = preset.source;
    api.get("/enquiries/report", { params }).then((r) => setData(r.data));
  }, [branchParam, start, end, preset.status, preset.source]);
  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const s = data?.summary || { total: 0, converted: 0, conversion_rate: 0, by_status: {}, by_source: [] };

  const csvExport = () => downloadCsv("enquiry_report",
    ["S.No", "Customer", "Mobile", "Branch", "Source", "Service", "Staff", "Enquiry Date", "Follow-up", "Status"],
    rows.map((r, i) => [i + 1, r.customer_name, r.mobile, r.branch_name, r.source, r.interested_service, r.assigned_staff_name, r.enquiry_date, r.follow_up_date, r.status]));

  const pdfExport = async () => {
    const params = { branch_id: branchParam || undefined, start, end };
    if (preset.status) params.status = preset.status;
    if (preset.source) params.source = preset.source;
    try {
      const res = await api.get("/enquiries/report.pdf", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "enquiry_report.pdf";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  const th = "text-muted-foreground text-left border-b border-border";
  return (
    <div>
      <button onClick={onBack} data-testid="enquiry-report-back" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3"><ChevronLeft size={16} /> All Reports</button>
      <PageHeader title={report.name} subtitle={report.desc}
        actions={
          <div className="flex gap-2">
            <Button onClick={csvExport} variant="outline" className="rounded-sm" data-testid="enq-report-csv"><Download size={15} className="mr-1" /> Excel/CSV</Button>
            <Button onClick={pdfExport} variant="outline" className="rounded-sm" data-testid="enq-report-pdf"><Download size={15} className="mr-1" /> PDF</Button>
            <Button onClick={() => window.print()} variant="outline" className="rounded-sm" data-testid="enq-report-print"><Printer size={15} className="mr-1" /> Print</Button>
          </div>
        } />

      <Card className="p-4 bg-card border-border mb-6">
        <Badge variant="outline" className="border-primary text-primary mb-3" data-testid="enq-report-branch-chip">Showing: {branchLabel}</Badge>
        <ReportDateFilter r={rr} prefix="enq-report" />
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total Enquiries" value={s.total} icon={Users} testid="enq-kpi-total" />
        <StatCard label="Converted" value={s.converted} icon={CheckCircle2} testid="enq-kpi-converted" />
        <StatCard label="Conversion Rate" value={`${s.conversion_rate}%`} icon={TrendingUp} testid="enq-kpi-rate" />
        <StatCard label="Lost" value={s.by_status?.Lost || 0} icon={XCircle} testid="enq-kpi-lost" />
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <Card className="p-4 bg-card border-border"><h4 className="text-sm text-muted-foreground mb-2">By Status</h4>
          <div className="flex flex-wrap gap-2">{Object.entries(s.by_status || {}).map(([k, v]) => <Badge key={k} variant="outline">{k}: <span className="text-primary ml-1">{v}</span></Badge>)}</div>
        </Card>
        <Card className="p-4 bg-card border-border md:col-span-2"><h4 className="text-sm text-muted-foreground mb-3">Source Analytics — enquiries vs converted (branch-wise)</h4>
          <table className="w-full text-sm">
            <thead><tr className="text-muted-foreground text-left border-b border-border">
              <th className="py-1.5">Source</th><th className="text-right">Enquiries</th><th className="text-right">Converted</th><th className="text-right">Conversion %</th>
            </tr></thead>
            <tbody>
              {(s.by_source || []).map((x) => (
                <tr key={x.source} className="border-b border-border/40" data-testid={`enq-source-row-${x.source}`}>
                  <td className="py-1.5">{x.source}</td>
                  <td className="text-right">{x.count}</td>
                  <td className="text-right text-green-400">{x.converted}</td>
                  <td className="text-right text-primary">{x.conversion_rate}%</td>
                </tr>
              ))}
              {!(s.by_source || []).length && <tr><td colSpan={4} className="text-center text-muted-foreground py-4">No data</td></tr>}
            </tbody>
          </table>
        </Card>
      </div>

      <Card className="bg-card border-border overflow-x-auto">
        <table className="w-full text-sm whitespace-nowrap">
          <thead><tr className={th}><th className="py-2 px-3">Customer</th><th className="px-3">Mobile</th><th className="px-3">Branch</th><th className="px-3">Source</th><th className="px-3">Service</th><th className="px-3">Staff</th><th className="px-3">Enquiry</th><th className="px-3">Follow-up</th><th className="px-3">Status</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-border/50" data-testid={`enq-report-row-${r.id}`}>
                <td className="py-2 px-3 font-medium">{r.customer_name}</td>
                <td className="px-3">{r.mobile}</td>
                <td className="px-3 text-muted-foreground">{r.branch_name}</td>
                <td className="px-3">{r.source}</td>
                <td className="px-3 text-muted-foreground">{r.interested_service || "—"}</td>
                <td className="px-3 text-muted-foreground">{r.assigned_staff_name || "—"}</td>
                <td className="px-3 text-xs">{r.enquiry_date}</td>
                <td className="px-3 text-xs">{r.follow_up_date || "—"}</td>
                <td className="px-3">{r.status}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={9} className="text-center text-muted-foreground py-10">No enquiries in this range.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
